import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import TiktokIcon from "@/components/social-accounts/tiktok-icon"
import TiktokOAuthStartForm from "@/components/social-accounts/tiktok-oauth-start-form"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { brandTiktokConnectPath, brandTiktokConnectionPath } from "@/routes"
import type { BrandIntegrationsTiktokProps } from "@/types/integrations"

/**
 * OKS-462 — Brand Settings → Integrations → TikTok connection card.
 *
 * Renders the brand's TikTok connection status + the status-appropriate
 * action (admin only). All five UX states (UX Spec §3):
 *   - **not connected** (`connection === null`) → neutral badge + Connect
 *   - **connected**  → secondary badge + @handle + Disconnect
 *   - **expired**    → outline badge + Re-connect
 *   - **error**      → destructive badge + Alert reason + Re-connect
 *   - non-admin (`!canManage`) → action row DOM-absent (read-only)
 *
 * Connect / Re-connect use the NATIVE top-level `<TiktokOAuthStartForm>`
 * (cross-origin OAuth cannot ride an Inertia XHR). Disconnect is a
 * same-origin Inertia `<Form method="delete">` inside a destructive
 * `AlertDialog`. Both action-triggering elements disable on submit to
 * prevent double-submit.
 *
 * Status is conveyed by TEXT + badge (never colour alone — a11y).
 */
export function TiktokIntegrationCard({
  connection,
  failure_reason,
  canManage,
}: BrandIntegrationsTiktokProps & { canManage: boolean }) {
  const { t } = useTranslation()

  const status = connection?.status ?? "not_connected"
  const isConnected = connection?.status === "connected"
  const isErrored = connection?.status === "error"

  const badge = (() => {
    switch (status) {
      case "connected":
        return {
          variant: "secondary" as const,
          label: t("settings.brand.integrations.status.connected"),
        }
      case "expired":
        return {
          variant: "outline" as const,
          label: t("settings.brand.integrations.status.expired"),
        }
      case "error":
        return {
          variant: "destructive" as const,
          label: t("settings.brand.integrations.status.error"),
        }
      default:
        return {
          variant: "outline" as const,
          label: t("settings.brand.integrations.status.not_connected"),
        }
    }
  })()

  return (
    <Card data-testid="brand-tiktok-integration-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <TiktokIcon width={20} height={20} aria-hidden="true" />
          {t("settings.brand.integrations.tiktok.label")}
        </CardTitle>
        <Badge variant={badge.variant} data-testid="brand-tiktok-status-badge">
          {badge.label}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {isConnected ? (
          <div className="min-w-0" data-testid="brand-tiktok-handle">
            <p className="truncate font-medium">
              {t("settings.brand.integrations.tiktok.connected_as", {
                handle: connection?.handle,
              })}
            </p>
            {connection?.display_name ? (
              <p className="text-muted-foreground truncate text-sm">
                {connection.display_name}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            {t("settings.brand.integrations.tiktok.body_disconnected")}
          </p>
        )}

        {/* Error reason line — actionable, using the plain-language
            failure reason from the post-redirect flash. */}
        {isErrored && failure_reason ? (
          <Alert variant="destructive" data-testid="brand-tiktok-error-alert">
            <AlertDescription>
              {t(`settings.brand.integrations.errors.${failure_reason}`)}
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Action row — admin only; DOM-absent for members. */}
        {canManage ? (
          <div
            className="flex flex-col gap-2 sm:flex-row sm:justify-end"
            data-testid="brand-tiktok-actions"
          >
            {isConnected ? (
              <DisconnectAction />
            ) : (
              <ConnectAction reconnect={connection != null} />
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

/** Native top-level form → BE 302 → TikTok authorize (cross-origin). */
function ConnectAction({ reconnect }: { reconnect: boolean }) {
  const { t } = useTranslation()

  return (
    <TiktokOAuthStartForm
      action={brandTiktokConnectPath()}
      data-testid="brand-tiktok-connect-form"
      className="w-full sm:w-auto"
    >
      <Button
        type="submit"
        // UX Spec §5 — ≥44px touch target on mobile (shadcn default is
        // h-9/36px); tighten back to the default height from `sm` up.
        className="h-11 w-full sm:h-9 sm:w-auto"
        data-testid="brand-tiktok-connect"
      >
        {reconnect
          ? t("settings.brand.integrations.tiktok.reconnect")
          : t("settings.brand.integrations.tiktok.connect")}
      </Button>
    </TiktokOAuthStartForm>
  )
}

/** Destructive AlertDialog → same-origin Inertia delete. */
function DisconnectAction() {
  const { t } = useTranslation()

  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            // UX Spec §5 — ≥44px touch target on mobile.
            className="h-11 w-full sm:h-9 sm:w-auto"
            data-testid="brand-tiktok-disconnect-trigger"
          >
            {t("settings.brand.integrations.tiktok.disconnect")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="brand-tiktok-disconnect-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("settings.brand.integrations.tiktok.disconnect_confirm_title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("settings.brand.integrations.tiktok.disconnect_confirm_body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={brandTiktokConnectionPath()}
          method="delete"
          options={{ preserveScroll: true }}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <AlertDialogCancel
                render={
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="brand-tiktok-disconnect-cancel"
                  >
                    {t(
                      "settings.brand.integrations.tiktok.disconnect_confirm_cancel",
                    )}
                  </Button>
                }
              />
              {/* Plain submit — NOT AlertDialogAction (which is a
                  Close and would tear down the form before Inertia
                  submits). The successful-delete redirect re-renders
                  the page, which unmounts the dialog. */}
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid="brand-tiktok-disconnect-confirm"
              >
                {processing
                  ? t("settings.brand.integrations.tiktok.disconnecting")
                  : t(
                      "settings.brand.integrations.tiktok.disconnect_confirm_cta",
                    )}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default TiktokIntegrationCard
